import { Channel } from './channel';
import { hasCompleteArguments } from './has_complete_arguments';
import { ResolvedToolDefinition } from './tool_definition';

export interface ToolCallFunction {
  name: string;
  args: string;
  isPartial: boolean;
}

interface ToolCallResult {
  index: number;
  result: string;
}

export class ToolCallRunner {
  private currentChannel: Channel<string> | null = null;
  private lastPushedArgs = '';
  private pushedCallsCount = 0;
  private readonly runningToolCalls: Promise<string>[] = [];

  constructor(
    private tools: ResolvedToolDefinition[] | undefined,
    private onCompleted: (index: number) => void,
  ) {}

  runToolCalls(toolCalls: readonly ToolCallFunction[]): void {
    const pending = toolCalls.slice(this.pushedCallsCount);

    for (const toolCall of pending) {
      if (!this.currentChannel) {
        this.currentChannel = new Channel<string>();
        this.lastPushedArgs = '';
        this.startToolCall(toolCall, this.currentChannel);
      }

      if (toolCall.args !== this.lastPushedArgs) {
        this.lastPushedArgs = toolCall.args;
        this.currentChannel.push(toolCall.args);
      }

      if (toolCall.isPartial) break;

      this.cleanup();

      this.pushedCallsCount++;
    }
  }

  cleanup(): void {
    this.currentChannel?.close();
    this.currentChannel = null;
  }

  async *awaitEach(): AsyncIterable<ToolCallResult> {
    for (const [index, promise] of this.runningToolCalls.entries()) {
      yield { index, result: await promise };
    }
  }

  private startToolCall(
    { name }: ToolCallFunction,
    channel: Channel<string>,
  ): void {
    const definition = this.tools?.find((candidate) => candidate.name === name);
    const index = this.runningToolCalls.length;

    this.runningToolCalls.push(
      (async () => {
        const result = await runTool(definition, name, channel);
        this.onCompleted(index);
        return result;
      })(),
    );
  }
}

async function runTool(
  definition: ResolvedToolDefinition | undefined,
  name: string,
  argsChannel: AsyncIterable<string>,
): Promise<string> {
  if (!definition) return `Error: unknown tool "${name}"`;

  if (definition.functionStreaming) {
    return definition.functionStreaming(argsChannel);
  }

  let finalArgs = '';
  for await (const args of argsChannel) finalArgs = args;

  // a call with incomplete arguments is discarded downstream. thus skip tool execution and return empty result.
  if (!hasCompleteArguments({ function: { arguments: finalArgs } })) return '';

  return definition.function(finalArgs);
}
